/* ==========================================================================
   VARENZO — theme JS
   Header shrink-on-scroll, mega menu, mobile nav drawer + accordions, hero
   scroll-linked phase reveal, product filter, and cookie/privacy controls
   wired to Shopify's Customer Privacy API.
   Accessibility helpers (focus management, inert handling) live in
   varenzo-a11y.js.
   ========================================================================== */
(function () {
  "use strict";

  /* Polite live-region announcer shared by other scripts. */
  var liveRegion = null;
  window.VarenzoAnnounce = function (message) {
    try {
      if (!liveRegion) {
        liveRegion = document.createElement("div");
        liveRegion.className = "v-sr-only";
        liveRegion.setAttribute("role", "status");
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.setAttribute("aria-atomic", "true");
        document.body.appendChild(liveRegion);
      }
      liveRegion.textContent = "";
      window.setTimeout(function () { liveRegion.textContent = message; }, 60);
    } catch (e) { /* ignore */ }
  };

  document.addEventListener("DOMContentLoaded", init);
  if (document.readyState !== "loading") init();

  var initialized = false;
  function init() {
    if (initialized) return;
    initialized = true;

    var header = document.getElementById("v-header");
    var megaMenu = document.getElementById("v-mega-menu");
    var megaTriggers = Array.prototype.slice.call(document.querySelectorAll("[data-mega-trigger]"));
    var mobileMenuToggle = document.getElementById("v-mobile-menu-toggle");
    var mobileNavDrawer = document.getElementById("v-mobile-nav-drawer");

    var megaOpenTimer, megaCloseTimer, activeMegaType = null;

    /* Horizon scrolls the page inside `.page-wrapper` on desktop and on
       `window` on mobile, so scroll handlers bind to both. */
    function varenzoScrollTop() {
      var wrapper = document.querySelector(".page-wrapper");
      var wrapperTop = wrapper ? wrapper.scrollTop : 0;
      var winTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      return Math.max(wrapperTop, winTop);
    }
    function varenzoBindScroll(handler) {
      window.addEventListener("scroll", handler, { passive: true });
      var wrapper = document.querySelector(".page-wrapper");
      if (wrapper) wrapper.addEventListener("scroll", handler, { passive: true });
    }

    function updateHeaderHeight() {
      if (!header) return;
      var h = header.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty("--v-header-height", h + "px");
    }
    if (header) {
      varenzoBindScroll(function () {
        header.classList.toggle("scrolled", varenzoScrollTop() > 50);
        updateHeaderHeight();
      });
      if ("ResizeObserver" in window) new ResizeObserver(updateHeaderHeight).observe(header);
      window.addEventListener("resize", updateHeaderHeight);
      updateHeaderHeight();
    }

    /* The menu is a disclosure, not an ARIA menu. */
    megaTriggers.forEach(function (t) { t.removeAttribute("aria-haspopup"); });

    function openMegaMenu(type, immediate) {
      if (!megaMenu || window.matchMedia("(max-width:860px)").matches) return;
      clearTimeout(megaCloseTimer);
      var swap = megaMenu.classList.contains("is-open") && activeMegaType !== type;
      activeMegaType = type;

      function commit() {
        if (swap) megaMenu.classList.add("is-swapping");
        showMegaPanel(type);
        if (swap) requestAnimationFrame(function () { megaMenu.classList.remove("is-swapping"); });
        updateHeaderHeight();
        megaMenu.classList.add("has-opened");
        megaMenu.classList.add("is-open");
        megaMenu.setAttribute("aria-hidden", "false");
        megaTriggers.forEach(function (t) {
          t.setAttribute("aria-expanded", t.getAttribute("data-mega-trigger") === type ? "true" : "false");
        });
      }
      clearTimeout(megaOpenTimer);
      if (immediate || swap) commit();
      else megaOpenTimer = setTimeout(commit, 100);
    }

    function showMegaPanel(type) {
      document.querySelectorAll("[data-mega-panel]").forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-mega-panel") !== type;
      });
    }

    function closeMegaMenu(returnFocus) {
      if (!megaMenu) return;
      clearTimeout(megaOpenTimer);
      clearTimeout(megaCloseTimer);
      var trigger = megaTriggers.filter(function (t) { return t.getAttribute("data-mega-trigger") === activeMegaType; })[0];
      megaMenu.classList.remove("is-open", "is-swapping");
      megaMenu.setAttribute("aria-hidden", "true");
      megaTriggers.forEach(function (t) { t.setAttribute("aria-expanded", "false"); });
      if (returnFocus && trigger) trigger.focus();
      activeMegaType = null;
    }

    function scheduleMegaClose() {
      clearTimeout(megaCloseTimer);
      megaCloseTimer = setTimeout(function () { closeMegaMenu(false); }, 200);
    }

    megaTriggers.forEach(function (trigger) {
      var type = trigger.getAttribute("data-mega-trigger");
      trigger.addEventListener("pointerenter", function (e) { if (e.pointerType !== "touch") openMegaMenu(type, false); });
      trigger.addEventListener("pointerleave", scheduleMegaClose);
      trigger.addEventListener("focus", function () { openMegaMenu(type, true); });
      trigger.addEventListener("click", function () { closeMegaMenu(false); });
    });
    if (megaMenu) {
      megaMenu.addEventListener("pointerenter", function () { clearTimeout(megaCloseTimer); });
      megaMenu.addEventListener("pointerleave", scheduleMegaClose);
      document.addEventListener("pointerdown", function (e) {
        if (megaMenu.classList.contains("is-open") && !megaMenu.contains(e.target) && !megaTriggers.some(function (t) { return t.contains(e.target); })) {
          closeMegaMenu(false);
        }
      });
      document.addEventListener("focusin", function (e) {
        if (megaMenu.classList.contains("is-open") && !megaMenu.contains(e.target) && !megaTriggers.some(function (t) { return t.contains(e.target); })) {
          closeMegaMenu(false);
        }
      });
    }

    function closeMobileNav() {
      if (!mobileNavDrawer) return;
      mobileNavDrawer.classList.remove("is-open");
      mobileNavDrawer.setAttribute("aria-hidden", "true");
      if (mobileMenuToggle) {
        mobileMenuToggle.setAttribute("aria-expanded", "false");
        mobileMenuToggle.textContent = "Menu";
      }
      document.body.style.overflow = "";
    }
    function toggleMobileNav() {
      if (!mobileNavDrawer) return;
      var opening = !mobileNavDrawer.classList.contains("is-open");
      if (opening) {
        closeMegaMenu(false);
        updateHeaderHeight();
        mobileNavDrawer.classList.add("is-open");
        mobileNavDrawer.setAttribute("aria-hidden", "false");
        mobileMenuToggle.setAttribute("aria-expanded", "true");
        mobileMenuToggle.textContent = "Close";
        document.body.style.overflow = "hidden";
      } else closeMobileNav();
    }
    if (mobileMenuToggle) mobileMenuToggle.addEventListener("click", toggleMobileNav);
    document.querySelectorAll(".v-mobile-nav-accordion").forEach(function (button) {
      button.addEventListener("click", function () {
        var panel = document.getElementById(button.getAttribute("aria-controls"));
        var open = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", open ? "false" : "true");
        if (panel) panel.classList.toggle("open", !open);
      });
    });
    if (mobileNavDrawer) {
      mobileNavDrawer.querySelectorAll("a").forEach(function (link) {
        link.addEventListener("click", closeMobileNav);
      });
    }
    window.addEventListener("resize", function () {
      if (window.innerWidth > 860) closeMobileNav();
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        if (mobileNavDrawer && mobileNavDrawer.classList.contains("is-open")) {
          closeMobileNav();
          if (mobileMenuToggle) mobileMenuToggle.focus();
          return;
        }
        if (megaMenu && megaMenu.classList.contains("is-open")) closeMegaMenu(true);
      }
    });

    var hero = document.getElementById("v-hero");
    if (hero) {
      var phases = Array.prototype.slice.call(hero.querySelectorAll(".v-hero-phase"));
      var sequenceText = document.getElementById("v-sequence-text");
      var reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

      function showFinalPhase() {
        phases.forEach(function (p) { p.classList.remove("visible"); });
        if (phases[2]) phases[2].classList.add("visible");
      }

      var ticking = false;
      function updateHero() {
        if (reducedMotionQuery.matches) { showFinalPhase(); return; }
        var rect = hero.getBoundingClientRect();
        var scrollable = hero.offsetHeight - window.innerHeight;
        var distance = Math.min(Math.max(-rect.top, 0), scrollable);
        var progress = scrollable ? distance / scrollable : 0;

        phases.forEach(function (p) { p.classList.remove("visible"); });
        if (progress < 0.33) { if (phases[0]) phases[0].classList.add("visible"); }
        else if (progress < 0.67) { if (phases[1]) phases[1].classList.add("visible"); }
        else { if (phases[2]) phases[2].classList.add("visible"); }

        if (sequenceText) {
          if (progress < 0.22) sequenceText.textContent = "Scroll to reveal";
          else if (progress < 0.46) sequenceText.textContent = "Precision assembly";
          else if (progress < 0.70) sequenceText.textContent = "Stone activation";
          else sequenceText.textContent = "VARENZO";
        }
      }
      varenzoBindScroll(function () {
        if (!ticking) {
          requestAnimationFrame(function () { updateHero(); ticking = false; });
          ticking = true;
        }
      });
      if (reducedMotionQuery.addEventListener) reducedMotionQuery.addEventListener("change", updateHero);
      updateHero();
    }

    document.querySelectorAll("[data-filter-bar]").forEach(function (bar) {
      var gridId = bar.getAttribute("data-filter-bar");
      var grid = document.getElementById(gridId);
      if (!grid) return;
      var cards = Array.prototype.slice.call(grid.querySelectorAll("[data-category]"));
      bar.querySelectorAll("[data-filter]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          bar.querySelectorAll("[data-filter]").forEach(function (b) { b.classList.remove("active"); b.setAttribute("aria-pressed", "false"); });
          btn.classList.add("active");
          btn.setAttribute("aria-pressed", "true");
          var filter = btn.getAttribute("data-filter");
          var shown = 0;
          cards.forEach(function (card) {
            var show = filter === "All" || card.getAttribute("data-category") === filter;
            card.hidden = !show;
            if (show) shown++;
          });
          window.VarenzoAnnounce("Showing " + shown + (shown === 1 ? " product" : " products") + (filter === "All" ? "" : " in " + filter));
        });
      });
    });

    /* ---------------------------------------------------------------
       Cookie & privacy controls.
       Choices are sent to Shopify's Customer Privacy API
       (Shopify.customerPrivacy.setTrackingConsent) so that Shopify and
       any consent-aware app or pixel respects them. Global Privacy
       Control forces analytics, marketing and data sale/sharing off.
       --------------------------------------------------------------- */
    var CONSENT_KEY = "varenzo-consent-v2";
    var consentBanner = document.getElementById("v-cookie-consent");
    if (consentBanner) {
      var acceptBtn = document.getElementById("v-cookie-accept");
      var rejectBtn = document.getElementById("v-cookie-reject");
      var customizeBtn = document.getElementById("v-cookie-customize");
      var saveBtn = document.getElementById("v-cookie-save");
      var prefsPanel = document.getElementById("v-cookie-prefs");
      var analyticsToggle = document.getElementById("v-cookie-analytics");
      var marketingToggle = document.getElementById("v-cookie-marketing");
      var preferencesToggle = document.getElementById("v-cookie-preferences");
      var gpcNote = document.getElementById("v-cookie-gpc-note");
      var statusEl = document.getElementById("v-cookie-status");
      var title = document.getElementById("v-cookie-title");
      var gpc = !!navigator.globalPrivacyControl;
      var lastOpener = null;
      var openedManually = false;

      function readLocal() {
        try { return JSON.parse(localStorage.getItem(CONSENT_KEY)); } catch (e) { return null; }
      }
      function writeLocal(c) {
        try {
          localStorage.setItem(CONSENT_KEY, JSON.stringify({
            analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences,
            method: c.method, timestamp: Date.now()
          }));
        } catch (e) { /* storage unavailable */ }
      }
      function setStatus(msg) { if (statusEl) { statusEl.textContent = ""; window.setTimeout(function () { statusEl.textContent = msg; }, 60); } }

      function withPrivacy(cb, tries) {
        tries = tries || 0;
        var S = window.Shopify;
        if (S && S.customerPrivacy && typeof S.customerPrivacy.setTrackingConsent === "function") { cb(S.customerPrivacy); return; }
        if (S && typeof S.loadFeatures === "function" && !withPrivacy.loading) {
          withPrivacy.loading = true;
          S.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (err) {
            withPrivacy.loading = false;
            if (!err && S.customerPrivacy) cb(S.customerPrivacy); else cb(null);
          });
          return;
        }
        if (tries < 20) { window.setTimeout(function () { withPrivacy(cb, tries + 1); }, 250); return; }
        cb(null);
      }

      function pushToShopify(c, done) {
        withPrivacy(function (p) {
          if (!p) { done(false); return; }
          try {
            p.setTrackingConsent({
              analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences,
              sale_of_data: !!c.marketing
            }, function () { done(true); });
          } catch (e) { done(false); }
        });
      }

      function updateInputs(c) {
        if (analyticsToggle) analyticsToggle.checked = !!c.analytics;
        if (marketingToggle) marketingToggle.checked = !!c.marketing;
        if (preferencesToggle) preferencesToggle.checked = !!c.preferences;
      }
      if (gpc) {
        if (analyticsToggle) { analyticsToggle.checked = false; analyticsToggle.disabled = true; }
        if (marketingToggle) { marketingToggle.checked = false; marketingToggle.disabled = true; }
        if (gpcNote) gpcNote.hidden = false;
      }

      function showBanner() {
        consentBanner.classList.add("is-visible");
        consentBanner.setAttribute("aria-hidden", "false");
      }
      function hideBanner() {
        var hadFocus = consentBanner.contains(document.activeElement);
        consentBanner.classList.remove("is-visible");
        consentBanner.setAttribute("aria-hidden", "true");
        if (prefsPanel) prefsPanel.classList.remove("open");
        if (customizeBtn) customizeBtn.setAttribute("aria-expanded", "false");
        if (hadFocus) {
          var target = lastOpener && document.body.contains(lastOpener) ? lastOpener : document.getElementById("MainContent");
          if (target) { if (!target.hasAttribute("tabindex") && target.id === "MainContent") target.setAttribute("tabindex", "-1"); target.focus(); }
        }
        openedManually = false;
      }

      function applyChoice(c, method) {
        c = { analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences, method: method };
        if (gpc) { c.analytics = false; c.marketing = false; }
        writeLocal(c);
        updateInputs(c);
        hideBanner();
        setStatus("Saving your cookie preferences.");
        pushToShopify(c, function (ok) {
          setStatus(ok
            ? "Your cookie preferences have been saved."
            : "Your choice is saved in this browser, but we could not reach Shopify's privacy service to apply it. Please try again later.");
        });
      }

      function openPreferences(opener) {
        lastOpener = opener || null;
        openedManually = true;
        var saved = readLocal();
        function prefill(cur) {
          if (cur) updateInputs({ analytics: cur.analytics === "yes", marketing: cur.marketing === "yes", preferences: cur.preferences === "yes" });
          else if (saved) updateInputs(saved);
          if (gpc) updateInputs({ analytics: false, marketing: false, preferences: (cur ? cur.preferences === "yes" : saved && saved.preferences) });
        }
        prefill(null);
        var S = window.Shopify;
        if (S && S.customerPrivacy && typeof S.customerPrivacy.currentVisitorConsent === "function") {
          try {
            var cur = S.customerPrivacy.currentVisitorConsent();
            if (cur && (cur.analytics || cur.marketing || cur.preferences)) prefill(cur);
          } catch (e) { /* ignore */ }
        }
        if (prefsPanel) prefsPanel.classList.add("open");
        if (customizeBtn) customizeBtn.setAttribute("aria-expanded", "true");
        showBanner();
        window.setTimeout(function () { if (title) title.focus(); }, 30);
      }
      window.VarenzoConsent = { open: openPreferences };

      if (acceptBtn) acceptBtn.addEventListener("click", function () { applyChoice({ analytics: true, marketing: true, preferences: true }, "accept_all"); });
      if (rejectBtn) rejectBtn.addEventListener("click", function () { applyChoice({ analytics: false, marketing: false, preferences: false }, "reject_all"); });
      if (customizeBtn && prefsPanel) customizeBtn.addEventListener("click", function () {
        var open = prefsPanel.classList.toggle("open");
        customizeBtn.setAttribute("aria-expanded", open ? "true" : "false");
      });
      if (saveBtn) saveBtn.addEventListener("click", function () {
        applyChoice({
          analytics: !!(analyticsToggle && analyticsToggle.checked),
          marketing: !!(marketingToggle && marketingToggle.checked),
          preferences: !!(preferencesToggle && preferencesToggle.checked)
        }, "save_preferences");
      });
      consentBanner.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && openedManually) { e.stopPropagation(); hideBanner(); }
      });

      var existing = readLocal();
      if (existing) { updateInputs(existing); }
      else if (gpc) { applyChoice({ analytics: false, marketing: false, preferences: false }, "gpc"); }
      else { showBanner(); }
    }
  }
})();
